import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import { useHistory, Link } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  getUsers,
  deleteUser,
  updateUser,
  enableUser,
  disableUser,
  handleUserManagementError
} from "@/services/userManagement";
import { getLoginHistory } from "@/services/loginHistoryService";
import CreateUserModal from "@/components/CreateUserModal";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  Search,
  Plus,
  RefreshCw,
  Settings,
  LogOut,
  UserPlus,
  Shield,
  Activity,
  Calendar,
  Mail,
  MoreHorizontal,
  Edit3,
  UserCheck,
  UserX,
  Trash2,
  Lock,
  Clock
} from "lucide-react";

const AdminUsers = () => {
  const { adminUser, adminLogout } = useAdminAuth();
  const [adminEmail, setAdminEmail] = useState("");
  const history = useHistory();
  const initialTab = new URLSearchParams(window.location.search).get("tab") || "users";
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);
  const [loginHistoryError, setLoginHistoryError] = useState(null);
  const [historyEmailFilter, setHistoryEmailFilter] = useState("");
  const [historyStartDate, setHistoryStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [historyEndDate, setHistoryEndDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const emailClaim = session.tokens.idToken.payload.email;
        setAdminEmail(emailClaim);
      } catch (err) {
        console.warn("Could not fetch admin email:", err);
      }
    })();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = await getUsers();
      setUsers(userData);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to load users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter((user) =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLogout = async () => {
    await adminLogout();
    history.push("/admin/login");
  };

  const handleRefresh = async () => {
    await fetchUsers();
  };

  const handleDeleteUser = async (userEmail) => {
    if (window.confirm(`Are you sure you want to delete user ${userEmail}?`)) {
      try {
        await deleteUser(userEmail);
        await fetchUsers();
      } catch (err) {
        console.error("Error deleting user:", err);
        setError(handleUserManagementError(err));
      }
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async (userEmail, updateData) => {
    try {
      await updateUser(userEmail, updateData);
      setIsEditModalOpen(false);
      setEditingUser(null);
      await fetchUsers();
    } catch (err) {
      console.error("Error updating user:", err);
      setError(handleUserManagementError(err));
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      if (user.enabled) {
        await disableUser(user.email);
      } else {
        await enableUser(user.email);
      }
      await fetchUsers();
    } catch (err) {
      console.error("Error toggling user status:", err);
      setError(handleUserManagementError(err));
    }
  };

  const fetchLoginHistory = async () => {
    try {
      setLoginHistoryLoading(true);
      setLoginHistoryError(null);
      const startISO = new Date(historyStartDate).toISOString();
      const endISO = new Date(historyEndDate + "T23:59:59").toISOString();
      const records = await getLoginHistory(startISO, endISO, historyEmailFilter || null);
      setLoginHistory(records);
    } catch (err) {
      console.error("Error fetching login history:", err);
      setLoginHistoryError("Failed to load login history. Please try again.");
    } finally {
      setLoginHistoryLoading(false);
    }
  };

  const getEnabledColor = (enabled) => {
    return enabled 
      ? 'bg-emerald-100 text-emerald-800 border-emerald-200' 
      : 'bg-red-100 text-red-800 border-red-200';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Beautiful Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                 TVIQ Admin Portal
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 px-3 py-2 bg-slate-100 rounded-lg">
                  <Mail className="h-4 w-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">
                    {adminEmail || adminUser?.username}
                  </span>
                </div>
                <Link to={"/admin/change-password"}>
                  <Button variant="outline" size="sm" className="text-slate-600 border-slate-200 hover:bg-slate-50">
                    <Settings className="h-4 w-4 mr-2" />
                    Change Password
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  onClick={handleLogout} 
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white/60 backdrop-blur-sm border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => { setActiveTab("users"); history.push("/admin/users"); }}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === "users"
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              Manage Users
            </button>
            <button
              onClick={() => {
                const now = new Date();
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(now.getDate() - 30);
                setHistoryStartDate(thirtyDaysAgo.toISOString().slice(0, 10));
                setHistoryEndDate(now.toISOString().slice(0, 10));
                setHistoryEmailFilter("");
                setActiveTab("loginHistory");
                history.push("/admin/users?tab=loginHistory");
                fetchLoginHistory();
              }}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === "loginHistory"
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              Login History
            </button>
            <button
              onClick={() => history.push("/admin/dashboard")}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200"
            >
              Manage Dashboards
            </button>
            
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {activeTab === "loginHistory" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                Login History
              </h2>
              <p className="text-slate-600 mt-2">View login attempts over the past 30 days</p>
            </div>

            {/* Filters */}
            <Card className="bg-white/80 backdrop-blur-sm border-slate-200/50 shadow-sm">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-slate-600">Start Date</label>
                    <input
                      type="date"
                      value={historyStartDate}
                      onChange={(e) => setHistoryStartDate(e.target.value)}
                      className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-slate-600">End Date</label>
                    <input
                      type="date"
                      value={historyEndDate}
                      onChange={(e) => setHistoryEndDate(e.target.value)}
                      className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                    <label className="text-xs font-medium text-slate-600">Filter by User Email</label>
                    <Input
                      placeholder="e.g. user@example.com"
                      value={historyEmailFilter}
                      onChange={(e) => setHistoryEmailFilter(e.target.value)}
                    />
                  </div>
                  <Button
                    onClick={fetchLoginHistory}
                    disabled={loginHistoryLoading}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loginHistoryLoading ? "animate-spin" : ""}`} />
                    {loginHistoryLoading ? "Loading..." : "Fetch"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <Card className="bg-white/80 backdrop-blur-sm border-slate-200/50 shadow-sm">
              <CardContent className="p-0">
                {loginHistoryError && (
                  <div className="p-4 text-red-600 text-sm">{loginHistoryError}</div>
                )}
                {!loginHistoryLoading && !loginHistoryError && loginHistory.length === 0 && (
                  <div className="p-8 text-center text-slate-500">No records found for the selected date range.</div>
                )}
                {loginHistory.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">User</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Time</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Error Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {loginHistory.map((record) => (
                          <tr key={record.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-6 py-4 text-sm text-slate-700">{record.userEmail}</td>
                            <td className="px-6 py-4 text-sm text-slate-500">
                              {new Date(record.timestamp).toLocaleString()}
                            </td>
                            <td className="px-6 py-4">
                              {record.status === "SUCCESS" ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Success</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-800 border border-red-200">Failed</Badge>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-500">
                              {record.status === "FAILED" ? record.errorMessage || record.errorCode || "—" : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "users" && <div className="space-y-8">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                User Management
              </h2>
              <p className="text-slate-600 mt-2">
                Manage user accounts, and permissions
              </p>
            </div>
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                onClick={handleRefresh} 
                disabled={loading}
                className="border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
              <Button
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-200"
                onClick={() => setIsModalOpen(true)}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add New User
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-white/80 backdrop-blur-sm border-slate-200/50 shadow-sm hover:shadow-md transition-all duration-200">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600">Total Users</p>
                    <p className="text-2xl font-bold text-slate-900">{users.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/80 backdrop-blur-sm border-slate-200/50 shadow-sm hover:shadow-md transition-all duration-200">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <UserCheck className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600">Active Users</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {users.filter(u => u.enabled).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/80 backdrop-blur-sm border-slate-200/50 shadow-sm hover:shadow-md transition-all duration-200">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <UserX className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600">Disabled Users</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {users.filter(u => !u.enabled).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>


          </div>

          {/* Security Warning */}
          <Card className="bg-yellow-50/80 backdrop-blur-sm border-yellow-200/50 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Shield className="h-4 w-4 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Security Notice: You cannot disable or delete admin accounts (including your own). This prevents accidental lockout and maintains system security.

                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Users Table */}
          <Card className="bg-white/80 backdrop-blur-sm border-slate-200/50 shadow-sm">
            <CardHeader className="border-b border-slate-200/50">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-3">
                  <Users className="h-5 w-5 text-slate-600" />
                  <CardTitle className="text-xl font-semibold text-slate-900">
                    All Users
                  </CardTitle>
                  <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                    {filteredUsers.length} users
                  </Badge>
                </div>
                <div className="relative w-full sm:w-80">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {error && (
                <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              {loading && (
                <div className="flex justify-center items-center py-12">
                  <div className="flex items-center space-x-2 text-slate-600">
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    <span>Loading users...</span>
                  </div>
                </div>
              )}

              {!loading && !error && filteredUsers.length === 0 && (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-500 text-lg font-medium">
                    {searchTerm ? "No users found matching your search." : "No users found."}
                  </p>
                  <p className="text-slate-400 mt-2">
                    {searchTerm ? "Try adjusting your search terms." : "Create your first user to get started."}
                  </p>
                </div>
              )}

              {!loading && !error && filteredUsers.length > 0 && (
                <div className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50/50">
                        <tr>
                          <th className="text-left p-4 font-semibold text-slate-700">User</th>
                          <th className="text-left p-4 font-semibold text-slate-700">Cognito Group</th>
                          <th className="text-left p-4 font-semibold text-slate-700">Enabled</th>
                          <th className="text-left p-4 font-semibold text-slate-700">MFA</th>
                          <th className="text-left p-4 font-semibold text-slate-700">MFA Setup Date</th>
                          <th className="text-left p-4 font-semibold text-slate-700">Last Login</th>
                          <th className="text-left p-4 font-semibold text-slate-700">Created</th>
                          <th className="text-left p-4 font-semibold text-slate-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200/50">
                        {filteredUsers.map((user, index) => (
                          <tr key={`${user.username}-${index}`} className="hover:bg-slate-50/50 transition-colors duration-150">
                            <td className="p-4">
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                    user.cognitoGroup === 'Admins' 
                                      ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                                      : 'bg-gradient-to-r from-blue-500 to-indigo-500'
                                  }`}>
                                    <span className="text-white font-semibold text-sm">
                                      {user.email.split('@')[0].charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <p className="font-semibold text-slate-900">{user.email.split('@')[0]}</p>
                                    {user.email === adminEmail && (
                                      <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200 text-xs">
                                        Current User
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-600">{user.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              {user.cognitoGroup ? (
                                <Badge className={
                                  user.cognitoGroup === 'Admins'
                                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                                    : 'bg-green-100 text-green-800 border border-green-200'
                                }>
                                  {user.cognitoGroup === 'Admins' && <Shield className="h-3 w-3 mr-1" />}
                                  {user.cognitoGroup}
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800 border border-gray-200">
                                  Unknown
                                </Badge>
                              )}
                            </td>
                            <td className="p-4">
                              <div className="space-y-2">
                                <Badge className={`${getEnabledColor(user.enabled)} border`}>
                                  {user.enabled ? 'Enabled' : 'Disabled'}
                                </Badge>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center space-x-2">
                                <Lock className={`h-4 w-4 ${user.mfaEnabled ? 'text-emerald-500' : 'text-slate-400'}`} />
                                <Badge className={user.mfaEnabled
                                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                  : 'bg-slate-100 text-slate-600 border border-slate-200'
                                }>
                                  {user.mfaEnabled ? 'Yes' : 'No'}
                                </Badge>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center space-x-2 text-sm text-slate-600">
                                <Calendar className="h-4 w-4 flex-shrink-0" />
                                <span>
                                  {user.mfaSetupAt
                                    ? new Date(user.mfaSetupAt).toLocaleDateString()
                                    : user.mfaEnabled
                                      ? 'Not recorded'
                                      : '—'}
                                </span>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center space-x-2 text-sm text-slate-600">
                                <Clock className="h-4 w-4 flex-shrink-0" />
                                <span>
                                  {user.lastLoginAt
                                    ? new Date(user.lastLoginAt).toLocaleString()
                                    : 'Never'}
                                </span>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center space-x-2 text-sm text-slate-600">
                                <Calendar className="h-4 w-4" />
                                <span>
                                  {user.created ? new Date(user.created).toLocaleDateString() : 'N/A'}
                                </span>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center space-x-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleEditUser(user)}
                                  className="border-slate-200 text-slate-700 hover:bg-slate-50"
                                >
                                  <Edit3 className="h-4 w-4 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleUserStatus(user)}
                                  disabled={user.email === adminEmail || user.cognitoGroup === 'Admins'}

                                  className={`${
                                    user.enabled 
                                      ? "border-red-200 text-red-600 hover:bg-red-50" 
                                      : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                                  } ${
                                    (user.email === adminEmail || user.cognitoGroup === 'Admins')
                                      ? "opacity-50 cursor-not-allowed bg-gray-100" 
                                      : ""
                                  }`}
                                  title={
                                    user.email === adminEmail 
                                      ? "Cannot disable your own account" 
                                      : user.cognitoGroup === 'Admins' 
                                        ? "Cannot disable other admin accounts" 
                                        : ""
                                  }

                                >
                                  {user.enabled ? (
                                    <>
                                      <UserX className="h-4 w-4 mr-1" />
                                      Disable
                                    </>
                                  ) : (
                                    <>
                                      <UserCheck className="h-4 w-4 mr-1" />
                                      Enable
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteUser(user.email)}
                                  disabled={user.email === adminEmail || user.cognitoGroup === 'Admins'}
                                  className={`border-red-200 text-red-600 hover:bg-red-50 ${
                                    (user.email === adminEmail || user.cognitoGroup === 'Admins')
                                      ? "opacity-50 cursor-not-allowed bg-gray-100" 
                                      : ""
                                  }`}
                                  title={
                                    user.email === adminEmail 
                                      ? "Cannot delete your own account" 
                                      : user.cognitoGroup === 'Admins' 
                                        ? "Cannot delete other admin accounts" 
                                        : ""
                                  }
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>}
      </main>

      {/* Create User Modal */}
      {isModalOpen && (
        <CreateUserModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onUserCreated={fetchUsers}
        />
      )}

      {/* Edit User Modal */}
      {isEditModalOpen && editingUser && (
        <EditUserModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingUser(null);
          }}
          user={editingUser}
          onUserUpdated={handleUpdateUser}
        />
      )}
    </div>
  );
};

// Edit User Modal Component
const EditUserModal = ({ isOpen, onClose, user, onUserUpdated }) => {
  const [formData, setFormData] = useState({
    newEmail: user?.email || "",
    newPassword: "",
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({
    newEmail: "",
    newPassword: "",
  });

  const validateForm = () => {
    const errors = {
      newEmail: "",
      newPassword: "",
    };

    if (formData.newEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.newEmail)) {
      errors.newEmail = "Please enter a valid email address";
    }

    if (formData.newPassword && formData.newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters long";
    }

    setValidationErrors(errors);
    return Object.values(errors).every((error) => error === "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsUpdating(true);
    setError("");

    try {
      const updateData = {};
      if (formData.newEmail && formData.newEmail !== user.email) {
        updateData.newEmail = formData.newEmail;
      }
      if (formData.newPassword) {
        updateData.newPassword = formData.newPassword;
      }

      if (Object.keys(updateData).length > 0) {
        await onUserUpdated(user.email, updateData);
      }
    } catch (err) {
      console.error("Error updating user:", err);
      setError(err instanceof Error ? err.message : "Failed to update user");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto bg-white/95 backdrop-blur-sm border-slate-200/50 shadow-2xl">
        <CardHeader className="border-b border-slate-200/50">
          <CardTitle className="text-xl font-bold text-slate-900 flex items-center space-x-2">
            <Edit3 className="h-5 w-5 text-blue-600" />
            <span>Edit User</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Current Email (Read-only) */}
            <div>
              <Label className="text-sm font-medium text-slate-700">Current Email</Label>
              <Input
                value={user.email}
                disabled
                className="bg-slate-50 border-slate-200 text-slate-600"
              />
            </div>

            

            {/* New Password */}
            <div>
              <Label htmlFor="newPassword" className="text-sm font-medium text-slate-700">
                New Password (optional)
              </Label>
              <Input
                id="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={(e) => handleInputChange("newPassword", e.target.value)}
                placeholder="Leave blank to keep current password"
                className={validationErrors.newPassword ? "border-red-500" : ""}
              />
              {validationErrors.newPassword && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.newPassword}</p>
              )}
              <p className="text-slate-500 text-xs mt-1">
                Password must be at least 8 characters with uppercase, lowercase, number, and special character
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} disabled={isUpdating} className="border-slate-200 text-slate-700 hover:bg-slate-50">
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdating} className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                {isUpdating ? "Updating..." : "Update User"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUsers;